/**
 * Internal server-backed saved views hook.
 *
 * ⚠️ This file intentionally exports an INTERNAL hook used only by
 * `useSavedViewsUnified`. Do not import it elsewhere.
 */

"use client";

import { useOrganization } from "@/contexts/organization-context";
import { useAuthSafe } from "@/hooks/auth/use-auth-safe";
import { useEffect, useMemo } from "react";
import type { UseUnifiedSavedViewsOptions } from "./types";
import { useSavedViewsEffects } from "./use-saved-views-effects";
import { useSavedViewsHandlers } from "./use-saved-views-handlers";
import { useSavedViewsMutations } from "./use-saved-views-mutations";
import { useSavedViewsNormalization } from "./use-saved-views-normalization";
import { useSavedViewsQuery } from "./use-saved-views-query";
import { useSavedViewsState } from "./use-saved-views-state";

export function useUnifiedSavedViewsInternal<
  TFilters extends Record<string, unknown>,
>(options: UseUnifiedSavedViewsOptions<TFilters>) {
  const {
    viewType,
    currentFilters,
    onApplyView,
    defaultFilters,
    syncWithUrl = true,
    autoApplyDefault = false,
    includeOrgPresets = true,
    enabled = true,
  } = options;

  const { getToken, isSignedIn, isLoaded } = useAuthSafe();
  const { organizationId } = useOrganization();

  const queryKey = useMemo(
    () => ["unified-saved-views", organizationId, viewType],
    [organizationId, viewType],
  );

  const {
    selectedViewId,
    setSelectedViewId,
    selectedViewIdValue,
    applyingViewRef,
    currentFiltersRef,
    lastAppliedViewIdRef,
  } = useSavedViewsState<TFilters>({
    viewType,
    organizationId,
    syncWithUrl,
  });

  useEffect(() => {
    currentFiltersRef.current = currentFilters;
  }, [currentFilters, currentFiltersRef]);

  const {
    data: savedViewsResponse,
    isLoading,
    error,
  } = useSavedViewsQuery({
    viewType,
    organizationId: organizationId ?? null,
    includeOrgPresets: includeOrgPresets ?? true,
    enabled: enabled ?? true,
    isLoaded: isLoaded ?? false,
    isSignedIn: isSignedIn ?? false,
    getToken,
  });

  const { allViews } = useSavedViewsNormalization<TFilters>({
    savedViewsResponse,
    viewType,
  });

  const {
    createView,
    updateView,
    deleteView,
    createMutation,
    updateMutation,
    deleteMutation,
  } = useSavedViewsMutations({
    viewType,
    organizationId,
    getToken,
    queryKey,
  });

  const selectedView = useMemo(
    () => allViews.find((view) => view.id === selectedViewIdValue),
    [allViews, selectedViewIdValue],
  );

  const { isDirty, handleApplyView, handleSaveView, handleDeleteView } =
    useSavedViewsHandlers<TFilters>({
      allViews,
      selectedViewIdValue,
      selectedView,
      currentFilters,
      defaultFilters,
      viewType,
      onApplyView,
      applyingViewRef,
      lastAppliedViewIdRef,
      setSelectedViewId,
      createView,
      updateView,
      deleteView,
    });

  useSavedViewsEffects<TFilters>({
    autoApplyDefault: autoApplyDefault ?? false,
    isLoading,
    allViews,
    selectedViewId,
    selectedViewIdValue,
    enabled: enabled ?? true,
    isLoaded: isLoaded ?? false,
    isSignedIn: isSignedIn ?? false,
    syncWithUrl: syncWithUrl ?? true,
    applyingViewRef,
    lastAppliedViewIdRef,
    currentFiltersRef,
    onApplyView,
    handleApplyView,
    currentFilters,
  });

  return {
    views: allViews,
    selectedViewId: selectedViewIdValue,
    selectedView,
    isDirty,
    isLoading,
    error: error
      ? error instanceof Error
        ? error.message
        : "Failed to load saved views"
      : null,
    createView,
    updateView,
    deleteView,
    handleApplyView,
    handleSaveView,
    handleDeleteView,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}
